/**
 * The two ticks beside a token's name and the always-present project-info slots of its header (Bez, 2026-10-04). Pure, no React.
 *
 * Since 2026-10-09 (Bez) project info is free and both ticks are paid steps on approved info. Blue tick, "Verified token team":
 * the wallet that controls the token proved it through a blue tick request (`blueVerified`). Gold tick, "Verified by CADINU team":
 * the CADINU team reviewed the project (`cadinuVerified`). Free approved info alone shows no tick. An API older than free listing
 * has no `blueVerified`: there approved info itself was the paid blue tick, so it still reads as blue.
 *
 * The slots never disappear: Website, X, Telegram and CoinMarketCap, then a one-line description. A slot reads approved info first,
 * then the public index the overview carries (GeckoTerminal: website and socials, never CoinMarketCap); when neither has it the slot
 * stays in place, marked missing, and is itself the way to add project info.
 */
import type { IconName } from './icons'
import type { BasicInfo, Cad1Info, ProjectInfoResponse, TokenLinks } from './types'

export type Ticks = { info: boolean; team: boolean }

/** blue from the API's own flag; an older API without it: approved info was the paid blue tick */
const blueOf = (blueVerified: unknown) => blueVerified === true || blueVerified === undefined

export function ticksOf(r?: ProjectInfoResponse | null): Ticks {
  const approved = Boolean(r?.info)
  return { info: approved && blueOf(r?.blueVerified), team: approved && r?.cadinuVerified === true }
}

/** The ONE tick a token shows (Bez, 2026-10-04: "gold tick OR blue tick, not both"): gold when the CADINU team reviewed it, otherwise
 * blue when the token team proved control; null for neither (free approved info alone). */
export function tickOf(r?: ProjectInfoResponse | null): 'team' | 'info' | null {
  const t = ticksOf(r)
  return t.team ? 'team' : t.info ? 'info' : null
}

/** The same one tick on the /tokens cards and the search rows (Bez, 2026-10-04), as the list carries it: gold, blue or none. */
export type ListVerified = 'gold' | 'blue' | null

/**
 * One row of the token_pages list (`/tokens`: one row per token with CURRENT approved info, the same submission its page reads) to the
 * list's state: gold when `cadinuVerified` is exactly true, blue when `blueVerified` is true (an older API without the field: blue, as
 * approved info was the paid blue tick there), else none; no row, no tick.
 */
export function listVerifiedOf(row?: { [field: string]: unknown; cadinuVerified?: unknown; blueVerified?: unknown } | null): ListVerified {
  if (!row || typeof row !== 'object') return null
  if (row.cadinuVerified === true) return 'gold'
  return blueOf(row.blueVerified) ? 'blue' : null
}

/** the list's state as the tick's kind (gold = the CADINU team, blue = verified token team) */
export function tickKindOf(v?: ListVerified | string | null): 'team' | 'info' | null {
  return v === 'gold' ? 'team' : v === 'blue' ? 'info' : null
}

/**
 * The basic page (Bez, 2026-10-06): links and a logo the CADINU team copied from the project's own public profiles, NEVER a tick.
 * Only while the token has no approved info (approved info always wins); null otherwise. The tick functions above never read it.
 */
export function basicOf(r?: ProjectInfoResponse | null): BasicInfo | null {
  if (!r || r.info) return null
  const b = r.basicInfo
  return b && typeof b === 'object' ? b : null
}

/**
 * CAD1 launch info (Bez, 2026-10-10): what a CAD1 token's creator committed to at launch, bound by the token's on-chain hash and published
 * without review. NEVER a tick (the tick functions above never read it; the green CAD1 tick comes from the launcher registry). Only while
 * the token has no approved info; null otherwise.
 */
export function cad1InfoOf(r?: ProjectInfoResponse | null): Cad1Info | null {
  if (!r || r.info) return null
  const c = r.cad1Info
  return c && typeof c === 'object' ? c : null
}

/** The honest line a basic page carries wherever its links show, and the way into the (free) reviewed flow. */
export const BASIC_NOTE = 'Info from the project’s public profiles. Not verified.'
export const CLAIM_LABEL = 'Claim this page'

export type SlotKey = 'website' | 'twitter' | 'telegram' | 'coinmarketcap'
/** where a filled slot's value came from: the approved submission, the CAD1 launch info, the basic page (public profiles, kept by CADINU),
 * or the public index */
export type SlotSource = 'verified' | 'cad1' | 'basic' | 'public'
export type Slot = { key: SlotKey; label: string; short: string; icon: IconName; href: string | null; source: SlotSource | null }
export type DescriptionSlot = { text: string | null; source: SlotSource | null }

export const SLOTS: ReadonlyArray<{ key: SlotKey; label: string; short: string; icon: IconName }> = [
  { key: 'website', label: 'Website', short: 'Website', icon: 'globe' },
  { key: 'twitter', label: 'X', short: 'X', icon: 'x' },
  { key: 'telegram', label: 'Telegram', short: 'Telegram', icon: 'telegram' },
  { key: 'coinmarketcap', label: 'CoinMarketCap', short: 'CMC', icon: 'cmc' },
]

/** only https links ever become a href */
const safe = (u?: string | null): string | null => (typeof u === 'string' && /^https:\/\/[^\s"'<>]+$/i.test(u.trim()) ? u.trim() : null)

export function projectSlots(r: ProjectInfoResponse | null | undefined, publicLinks: TokenLinks | null | undefined): Slot[] {
  const a = r?.info ?? null
  const c = cad1InfoOf(r)
  const b = basicOf(r)
  return SLOTS.map((s) => {
    const verified = safe(a?.[s.key])
    // CoinMarketCap only ever comes from approved info: neither the CAD1 launch info, a basic page nor the public index carries it
    const launch = s.key === 'coinmarketcap' ? null : safe(c?.[s.key])
    const basic = s.key === 'coinmarketcap' ? null : safe(b?.[s.key])
    const pub = s.key === 'coinmarketcap' ? null : safe(publicLinks?.[s.key])
    const href = verified ?? launch ?? basic ?? pub
    return { ...s, href, source: verified ? 'verified' : launch ? 'cad1' : basic ? 'basic' : pub ? 'public' : null }
  })
}

/** The description as one line: the first paragraph, whitespace folded. The header clamps it to one visual line. */
export function oneLine(text?: string | null): string | null {
  if (typeof text !== 'string') return null
  const first = text.trim().split(/\n\s*\n/)[0] ?? ''
  const folded = first.replace(/\s+/g, ' ').trim()
  return folded || null
}

export function descriptionSlot(r: ProjectInfoResponse | null | undefined, publicDescription?: string | null): DescriptionSlot {
  const verified = oneLine(r?.info?.description)
  if (verified) return { text: verified, source: 'verified' }
  const launch = oneLine(cad1InfoOf(r)?.description)
  if (launch) return { text: launch, source: 'cad1' }
  const pub = oneLine(publicDescription)
  return pub ? { text: pub, source: 'public' } : { text: null, source: null }
}

/** "4 Oct 2026" for the gold tick's popover; null when the API gave no usable time */
export function sinceText(iso?: string | null): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}
